// Google Analytics 4 Event Tracking
// All tracking is gated by shouldEnableTracking() from runtimeEnvironment.ts

import { shouldEnableTracking, getRuntimeEnvironment } from '@/lib/runtimeEnvironment';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

// Check if gtag is available AND tracking is allowed
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function' && shouldEnableTracking();
};

// Generic event tracking
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, string | number | boolean>
): void => {
  if (!shouldEnableTracking()) {
    console.debug(`[Analytics blocked: ${getRuntimeEnvironment()}] ${eventName}`, eventParams);
    return;
  }
  if (isGtagAvailable()) {
    window.gtag!('event', eventName, eventParams);
    console.log(`[Analytics] Event: ${eventName}`, eventParams);
  }
};

// ============== CONTENT GATE EVENTS (STRATEGIC) ==============

/**
 * Track content gate events for blocked technical content.
 * Events: view_content_blocked, open_block_modal, click_register_from_block,
 *         close_block_modal, register_completed, login_completed
 */
export const trackContentGateEvent = (
  event:
    | 'view_content_blocked'
    | 'open_block_modal'
    | 'click_register_from_block'
    | 'close_block_modal'
    | 'register_completed'
    | 'login_completed',
  pagePath?: string
): void => {
  trackEvent(event, {
    page_path: pagePath || window.location.pathname,
  });
};

// Track CTA clicks to consultoria page
export const trackConsultoriaCTA = (
  location: string,
  ctaText?: string
): void => {
  trackEvent('cta_view_consultoria', {
    location,
    cta_text: ctaText || '',
    page_path: window.location.pathname,
  });
};

// Track contact CTA clicks
export const trackContactCTA = (
  location: string,
  subject?: string
): void => {
  trackEvent('cta_contact', {
    location,
    subject: subject || '',
    page_path: window.location.pathname,
  });
};

// Track upgrade to Plano de Acesso
export const trackUpgradeToAccessPlan = (
  location: string
): void => {
  trackEvent('upgrade_to_access_plan', {
    location,
    page_path: window.location.pathname,
  });
};

// Conversion tracking for scheduling CTAs
export const trackSchedulingCTA = (
  action: 'click' | 'scroll_to_calendly' | 'form_start' | 'form_submit',
  location: string,
  additionalParams?: Record<string, string | number>
): void => {
  trackEvent('scheduling_cta', {
    action,
    location,
    page_path: window.location.pathname,
    ...additionalParams,
  });
};

// Track page views (useful for SPAs)
export const trackPageView = (pagePath: string, pageTitle?: string): void => {
  if (isGtagAvailable()) {
    window.gtag!('config', getGAMeasurementId(), {
      page_path: pagePath,
      page_title: pageTitle,
    });
  }
};

// Track conversion goals
export const trackConversion = (
  conversionType: 'lead' | 'booking' | 'form_submission',
  value?: number,
  currency: string = 'EUR'
): void => {
  trackEvent('conversion', {
    conversion_type: conversionType,
    value: value || 0,
    currency,
  });
};

// Get GA Measurement ID from environment
const getGAMeasurementId = (): string => {
  return import.meta.env.VITE_GA_MEASUREMENT_ID || '';
};

// Pre-qualification form tracking
export const trackPreQualForm = (
  step: 'start' | 'complete' | 'skip',
  formData?: Record<string, string>
): void => {
  trackEvent('pre_qual_form', {
    step,
    has_cargo: !!formData?.cargo,
    has_empresa: !!formData?.empresa,
    has_objetivo: !!formData?.objetivo,
    has_tema: !!formData?.tema,
  });
};

// Hero CTA tracking
export const trackHeroCTA = (ctaType: 'primary' | 'secondary', ctaText: string): void => {
  trackEvent('hero_cta_click', {
    cta_type: ctaType,
    cta_text: ctaText,
    page_path: window.location.pathname,
  });
};

// Calendly widget interaction tracking
export const trackCalendlyInteraction = (
  action: 'widget_loaded' | 'time_selected' | 'booking_confirmed'
): void => {
  trackEvent('calendly_interaction', {
    action,
    page_path: window.location.pathname,
  });
};

// ============== CHAT ANALYTICS ==============

// Store chat session start time
let chatSessionStart: number | null = null;

// Track chat session open/close
export const trackChatSession = (
  action: 'open' | 'close',
  context?: string,
  messageCount?: number
): void => {
  if (action === 'open') {
    chatSessionStart = Date.now();
    trackEvent('chat_session', {
      action,
      context: context || 'general',
      page_path: window.location.pathname,
    });
  } else {
    const sessionDuration = chatSessionStart 
      ? Math.round((Date.now() - chatSessionStart) / 1000) 
      : 0;
    chatSessionStart = null;
    
    trackEvent('chat_session', {
      action,
      context: context || 'general',
      page_path: window.location.pathname,
      session_duration_seconds: sessionDuration,
      message_count: messageCount || 0,
    });
  }
};

// Track QuickChip/TechnicalChip selection
export const trackChatChipSelect = (
  chipLabel: string,
  chipContext: string
): void => {
  trackEvent('chat_chip_select', {
    chip_label: chipLabel,
    chip_context: chipContext,
    page_path: window.location.pathname,
  });
};

// Track pre-audit flow interactions
export const trackPreAuditFlow = (
  action: 'start' | 'step_complete' | 'complete' | 'reset',
  step?: string,
  answer?: string
): void => {
  trackEvent('pre_audit_flow', {
    action,
    step: step || '',
    answer: answer || '',
    page_path: window.location.pathname,
  });
};

// Track CTA clicks from chat (final conversion)
export const trackChatCTA = (
  ctaType: 'falar_tecnico' | 'saber_mais_auditoria' | 'agendar' | 'outro',
  context?: string
): void => {
  trackEvent('chat_cta_click', {
    cta_type: ctaType,
    context: context || 'general',
    page_path: window.location.pathname,
  });
  
  // Also track as conversion
  trackConversion('lead', undefined, 'EUR');
};

// Track chat message sent
export const trackChatMessage = (
  source: 'input' | 'chip' | 'pre_audit',
  messageLength?: number
): void => {
  trackEvent('chat_message', {
    source,
    message_length: messageLength || 0,
    page_path: window.location.pathname,
  });
};

// ============== PAYMENT ANALYTICS ==============

// Track payment success (conversion)
export const trackPaymentSuccess = (
  serviceName?: string,
  value?: number,
  currency: string = 'EUR'
): void => {
  // Track as purchase event (GA4 recommended event)
  trackEvent('purchase', {
    transaction_id: `txn_${Date.now()}`,
    value: value || 0,
    currency,
    item_name: serviceName || 'apoio_tecnico',
  });
  
  // Also track as conversion
  trackConversion('booking', value, currency);
  
  // Custom event for internal tracking
  trackEvent('payment_success', {
    service_name: serviceName || 'apoio_tecnico',
    value: value || 0,
    currency,
    page_path: window.location.pathname,
  });
};

// Track payment canceled/abandoned
export const trackPaymentCanceled = (
  serviceName?: string,
  reason?: string
): void => {
  trackEvent('payment_canceled', {
    service_name: serviceName || 'apoio_tecnico',
    reason: reason || 'user_abandoned',
    page_path: window.location.pathname,
  });
  
  // Track checkout abandonment for remarketing
  trackEvent('checkout_abandonment', {
    step: 'payment',
    service_name: serviceName || 'apoio_tecnico',
  });
};

// Track payment page view (for funnel analysis)
export const trackPaymentPageView = (
  pageType: 'success' | 'canceled' | 'checkout',
  serviceName?: string
): void => {
  trackEvent('payment_page_view', {
    page_type: pageType,
    service_name: serviceName || 'apoio_tecnico',
    page_path: window.location.pathname,
  });
};

// ============== ENHANCED ECOMMERCE (GA4) ==============

// Product/Service item type for ecommerce
export interface EcommerceItem {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  currency?: string;
}

// Track view_item_list (when viewing a list of products/services)
export const trackViewItemList = (
  listName: string,
  items: EcommerceItem[]
): void => {
  if (isGtagAvailable()) {
    window.gtag!('event', 'view_item_list', {
      item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
      item_list_name: listName,
      items: items.map((item, index) => ({
        ...item,
        index,
        currency: item.currency || 'EUR',
      })),
    });
    console.log(`[Analytics] view_item_list: ${listName}`, items);
  }
};

// Track view_item (when viewing a single product/service)
export const trackViewItem = (item: EcommerceItem): void => {
  if (isGtagAvailable()) {
    window.gtag!('event', 'view_item', {
      currency: item.currency || 'EUR',
      value: item.price || 0,
      items: [{
        ...item,
        currency: item.currency || 'EUR',
      }],
    });
    console.log(`[Analytics] view_item:`, item);
  }
};

// Track select_item (when clicking on a product/service in a list)
export const trackSelectItem = (
  listName: string,
  item: EcommerceItem
): void => {
  if (isGtagAvailable()) {
    window.gtag!('event', 'select_item', {
      item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
      item_list_name: listName,
      items: [{
        ...item,
        currency: item.currency || 'EUR',
      }],
    });
    console.log(`[Analytics] select_item from ${listName}:`, item);
  }
};

// Track add_to_cart (when selecting a plan/service)
export const trackAddToCart = (item: EcommerceItem): void => {
  if (isGtagAvailable()) {
    window.gtag!('event', 'add_to_cart', {
      currency: item.currency || 'EUR',
      value: item.price || 0,
      items: [{
        ...item,
        quantity: item.quantity || 1,
        currency: item.currency || 'EUR',
      }],
    });
    console.log(`[Analytics] add_to_cart:`, item);
  }
};

// Track begin_checkout (when starting the checkout process)
export const trackBeginCheckout = (
  items: EcommerceItem[],
  totalValue?: number
): void => {
  const value = totalValue || items.reduce((sum, item) => sum + (item.price || 0), 0);
  
  if (isGtagAvailable()) {
    window.gtag!('event', 'begin_checkout', {
      currency: 'EUR',
      value,
      items: items.map((item) => ({
        ...item,
        quantity: item.quantity || 1,
        currency: item.currency || 'EUR',
      })),
    });
    console.log(`[Analytics] begin_checkout:`, { value, items });
  }
};

// Track add_payment_info (when payment info is added)
export const trackAddPaymentInfo = (
  items: EcommerceItem[],
  paymentType: string = 'card'
): void => {
  const value = items.reduce((sum, item) => sum + (item.price || 0), 0);
  
  if (isGtagAvailable()) {
    window.gtag!('event', 'add_payment_info', {
      currency: 'EUR',
      value,
      payment_type: paymentType,
      items: items.map((item) => ({
        ...item,
        quantity: item.quantity || 1,
        currency: item.currency || 'EUR',
      })),
    });
    console.log(`[Analytics] add_payment_info:`, { paymentType, items });
  }
};

// Enhanced purchase tracking with full ecommerce data
export const trackPurchaseEnhanced = (
  transactionId: string,
  items: EcommerceItem[],
  totalValue?: number
): void => {
  const value = totalValue || items.reduce((sum, item) => sum + (item.price || 0), 0);
  
  if (isGtagAvailable()) {
    window.gtag!('event', 'purchase', {
      transaction_id: transactionId,
      currency: 'EUR',
      value,
      items: items.map((item) => ({
        ...item,
        quantity: item.quantity || 1,
        currency: item.currency || 'EUR',
      })),
    });
    console.log(`[Analytics] purchase:`, { transactionId, value, items });
  }
  
  // Also track as conversion
  trackConversion('booking', value, 'EUR');
};

// Plan catalog for easy tracking (updated 2026)
export const plansCatalog: Record<string, EcommerceItem> = {
  // Plano de Acesso (subscription)
  access: {
    item_id: 'access_monthly',
    item_name: 'Plano de Acesso',
    item_category: 'subscription',
    item_variant: 'mensal',
    price: 39,
    currency: 'EUR',
  },
  // Consulting plans (one-time)
  essential: {
    item_id: 'essential_oneoff',
    item_name: 'ESSENTIAL',
    item_category: 'consultoria',
    item_variant: 'pre_diagnostico',
    price: 249,
    currency: 'EUR',
  },
  pro: {
    item_id: 'pro_oneoff',
    item_name: 'PRO',
    item_category: 'consultoria',
    item_variant: 'analise_tecnica',
    price: 790,
    currency: 'EUR',
  },
  premium: {
    item_id: 'premium_oneoff',
    item_name: 'PREMIUM',
    item_category: 'consultoria',
    item_variant: 'private',
    price: 1490,
    currency: 'EUR',
  },
};

// ============== SCROLL DEPTH TRACKING ==============

// Track which thresholds have been reached in the current page session
const scrollDepthReached: Set<number> = new Set();

// Reset scroll depth tracking (call on route change)
export const resetScrollDepthTracking = (): void => {
  scrollDepthReached.clear();
};

// Track scroll depth at specific thresholds
export const trackScrollDepth = (
  threshold: number,
  pagePath?: string
): void => {
  // Avoid duplicate tracking for same threshold
  if (scrollDepthReached.has(threshold)) return;
  
  scrollDepthReached.add(threshold);
  
  trackEvent('scroll_depth', {
    percent: threshold,
    page_path: pagePath || window.location.pathname,
  });
};

// Initialize scroll depth observer for a page
export const initScrollDepthTracking = (
  thresholds: number[] = [25, 50, 75, 100],
  pagePath?: string
): (() => void) => {
  resetScrollDepthTracking();
  
  const handleScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    
    if (docHeight <= 0) return;
    
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);
    
    thresholds.forEach((threshold) => {
      if (scrollPercent >= threshold && !scrollDepthReached.has(threshold)) {
        trackScrollDepth(threshold, pagePath);
      }
    });
  };

  // Use passive listener for performance
  window.addEventListener('scroll', handleScroll, { passive: true });
  
  // Check initial scroll position
  handleScroll();
  
  // Return cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);
    resetScrollDepthTracking();
  };
};

// ============== GTM DATALAYER EVENTS (CONVERSION FUNNEL) ==============

// Push event directly to dataLayer for GTM processing (gated by environment)
const pushToDataLayer = (event: string, params?: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  if (!shouldEnableTracking()) {
    console.debug(`[DataLayer blocked: ${getRuntimeEnvironment()}] ${event}`, params);
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params });
  console.log(`[GTM DataLayer] ${event}`, params);
};

// Dedup guard: track which events have already fired per page session
const landingEventsFired = new Set<string>();

/** Reset landing event dedup (call on route change) */
export const resetLandingEventTracking = (): void => {
  landingEventsFired.clear();
};

/** Internal helper: fire a landing_auditoria_ event once per page session */
const fireLandingEvent = (suffix: string, params?: Record<string, unknown>): boolean => {
  const eventName = `landing_auditoria_${suffix}`;
  if (landingEventsFired.has(eventName)) return false;
  landingEventsFired.add(eventName);
  pushToDataLayer(eventName, {
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
    page_location: typeof window !== 'undefined' ? window.location.href : '',
    ...params,
  });
  return true;
};

// ──────────── LANDING AUDITORIA EVENTS ────────────

/**
 * landing_auditoria_hero_click — Hero CTA "Solicitar Análise Técnica Independente"
 */
export const trackLandingHeroClick = (): void => {
  fireLandingEvent('hero_click', {
    button_location: 'hero',
    page_type: 'landing_servico',
  });
};

/**
 * landing_auditoria_mid_cta_click — Mid-page CTA click
 */
export const trackLandingMidCtaClick = (): void => {
  fireLandingEvent('mid_cta_click', {
    button_location: 'mid_page',
  });
};

/**
 * landing_auditoria_final_cta_click — Final CTA click
 */
export const trackLandingFinalCtaClick = (): void => {
  fireLandingEvent('final_cta_click', {
    button_location: 'final_cta',
  });
};

/**
 * landing_auditoria_form_step_1 — User advances from step 1 to step 2
 */
export const trackLandingFormStep1 = (): void => {
  fireLandingEvent('form_step_1', {
    form_step: 1,
  });
};

/**
 * landing_auditoria_form_submit — Successful form submission (server confirmed).
 * Marked as Conversion in GA4. Only fires after API success.
 */
export const trackLandingFormSubmit = (params?: {
  tipo_edificio?: string;
  numero_pisos?: string;
  motivo_pedido?: string;
}): void => {
  fireLandingEvent('form_submit', {
    form_name: 'auditoria_tecnica',
    conversion_type: 'lead',
    tipo_edificio: params?.tipo_edificio || '',
    numero_pisos: params?.numero_pisos || '',
    motivo_pedido: params?.motivo_pedido || '',
  });
};

/**
 * landing_auditoria_scroll_75 — User scrolls ≥ 75% of the page
 */
export const trackLandingScroll75 = (percentScroll: number): void => {
  fireLandingEvent('scroll_75', {
    percent_scroll: percentScroll,
  });
};

/**
 * landing_auditoria_comparison_click — User interacts with comparison table
 */
export const trackLandingComparisonClick = (criteria: string): void => {
  fireLandingEvent('comparison_click', {
    comparison_criteria: criteria,
  });
};

/**
 * landing_auditoria_internal_link_click — User clicks a strategic internal link
 */
export const trackLandingInternalLinkClick = (linkHref: string, linkText: string): void => {
  // Allow multiple internal link clicks (different links)
  const eventName = `landing_auditoria_internal_link_click`;
  const dedupKey = `${eventName}_${linkHref}`;
  if (landingEventsFired.has(dedupKey)) return;
  landingEventsFired.add(dedupKey);
  pushToDataLayer(eventName, {
    page_path: window.location.pathname,
    link_url: linkHref,
    link_text: linkText,
  });
};

// ──────────── LEGACY EVENTS (kept for backward compat) ────────────

/**
 * signup_completed — fires when a user completes registration successfully.
 * Sent ONLY to dataLayer for GTM processing. No direct gtag/GA4 call.
 * Includes dedup guard to prevent double-firing on refresh.
 */
export const trackSignupCompleted = (params: {
  method: 'email' | 'google' | string;
  lead_source?: string;
  user_type?: string;
}): void => {
  // Dedup: skip if already fired in this session
  const dedupKey = 'signup_completed_fired';
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(dedupKey)) {
    console.log('[GTM DataLayer] signup_completed already fired this session, skipping');
    return;
  }

  pushToDataLayer('signup_completed', {
    method: params.method,
    lead_source: params.lead_source || 'organic',
    user_type: params.user_type || '',
    page_location: window.location.href,
    page_title: document.title,
    traffic_source: document.referrer || '(direct)',
    campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
  });

  // Mark as fired for this session
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(dedupKey, '1');
  }
};

/**
 * generate_lead — fires on successful paid inspection request.
 * Mark as Conversion in GA4. DataLayer ONLY (no gtag redundancy).
 */
export const trackGenerateLead = (params: {
  tipo_edificio?: string;
  numero_pisos?: string;
  motivo_pedido?: string;
  plan?: string;
}): void => {
  pushToDataLayer('generate_lead', {
    lead_source: 'inspection_paid',
    plan: params.plan || '',
    page_location: window.location.href,
    page_title: document.title,
    traffic_source: document.referrer || '(direct)',
    campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
    tipo_edificio: params.tipo_edificio || '',
    numero_pisos: params.numero_pisos || '',
    motivo_pedido: params.motivo_pedido || '',
  });
};

/**
 * begin_checkout_auditoria — fires on first field interaction in /pedido-auditoria
 * Used as micro-conversion for Google Ads optimization.
 */
export const trackBeginCheckoutAuditoria = (): void => {
  pushToDataLayer('begin_checkout_auditoria', {
    page_location: window.location.href,
    page_title: document.title,
    traffic_source: document.referrer || '(direct)',
    campaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
  });
};

/**
 * auditoria_high_intent — fires when user shows high engagement on /auditoria-elevadores:
 *   - scroll ≥ 75%, OR
 *   - time ≥ 90s, OR
 *   - CTA click "Solicitar Auditoria Técnica"
 */
export const trackAuditoriaHighIntent = (trigger: 'scroll_75' | 'time_90s' | 'cta_click', params?: {
  percent_scroll?: number;
  engagement_time?: number;
}): void => {
  pushToDataLayer('auditoria_high_intent', {
    trigger,
    page_location: window.location.href,
    source_medium: document.referrer || '(direct)',
    percent_scroll: params?.percent_scroll || 0,
    engagement_time: params?.engagement_time || 0,
  });
};

/**
 * form_abandon_auditoria — fires when user interacted with 2+ fields
 * but leaves /pedido-auditoria without completing generate_lead.
 */
export const trackFormAbandonAuditoria = (fieldsInteracted: number): void => {
  pushToDataLayer('form_abandon_auditoria', {
    page_location: window.location.href,
    fields_interacted: fieldsInteracted,
    traffic_source: document.referrer || '(direct)',
  });
};

// ============== TIME ON PAGE TRACKING ==============

// Track time spent on page at specific intervals
let timeOnPageStart: number | null = null;
let timeOnPageInterval: ReturnType<typeof setInterval> | null = null;
const timeThresholdsReached: Set<number> = new Set();

// Reset time tracking (call on route change)
export const resetTimeOnPageTracking = (): void => {
  timeOnPageStart = null;
  if (timeOnPageInterval) {
    clearInterval(timeOnPageInterval);
    timeOnPageInterval = null;
  }
  timeThresholdsReached.clear();
};

// Track time threshold reached
export const trackTimeOnPage = (
  seconds: number,
  pagePath?: string
): void => {
  if (timeThresholdsReached.has(seconds)) return;
  
  timeThresholdsReached.add(seconds);
  
  trackEvent('time_on_page', {
    seconds,
    page_path: pagePath || window.location.pathname,
  });
};

// Initialize time on page tracking
export const initTimeOnPageTracking = (
  thresholds: number[] = [10, 30, 60, 120, 180],
  pagePath?: string
): (() => void) => {
  resetTimeOnPageTracking();
  timeOnPageStart = Date.now();
  
  timeOnPageInterval = setInterval(() => {
    if (!timeOnPageStart) return;
    
    const elapsedSeconds = Math.floor((Date.now() - timeOnPageStart) / 1000);
    
    thresholds.forEach((threshold) => {
      if (elapsedSeconds >= threshold && !timeThresholdsReached.has(threshold)) {
        trackTimeOnPage(threshold, pagePath);
      }
    });
    
    // Stop checking after max threshold reached
    const maxThreshold = Math.max(...thresholds);
    if (elapsedSeconds >= maxThreshold) {
      if (timeOnPageInterval) {
        clearInterval(timeOnPageInterval);
        timeOnPageInterval = null;
      }
    }
  }, 1000);
  
  // Track page exit time
  const handleBeforeUnload = () => {
    if (timeOnPageStart) {
      const totalSeconds = Math.floor((Date.now() - timeOnPageStart) / 1000);
      trackEvent('page_exit', {
        total_seconds: totalSeconds,
        page_path: pagePath || window.location.pathname,
      });
    }
  };
  
  window.addEventListener('beforeunload', handleBeforeUnload);
  
  return () => {
    resetTimeOnPageTracking();
    window.removeEventListener('beforeunload', handleBeforeUnload);
  };
};
